import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { ArrowRight, ArrowUpCircle, Info, Layers, Loader2, TriangleAlert, X } from 'lucide-react';
import type { AppError } from '@hookmyapp/observability/errors';
import { useSetOverflow } from '@/components/billing/queries';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { getV2Plan, isV2PlanSlug, nextPlan, nextV2PlanSlug } from '@/lib/plans';
import type { BillingSubscription, OverflowMode } from '@/types/billing';

// AIT-594: the smallest cap worth offering. A cap of one top-up is spent in
// full by the first recharge, so the customer is "warned" on the charge itself
// and then paused when the next one is refused. This bounds what the picker
// OFFERS; it never rewrites a cap the customer already chose.
const MIN_TOP_UPS = 2;

type ChargingMode = 'auto_upgrade' | 'auto_top_up';

// AIT-420: the unit word is the org's own meter — a v2 org buys ACTIONS, not
// messages. Everything user-facing in this component reads it from the
// subscription contract (`usageUnit`), never from the plan slug.
type UsageUnit = 'messages' | 'actions';

function modesFor(unit: UsageUnit): { value: ChargingMode; title: string; desc: string; icon: LucideIcon }[] {
  return [
    {
      value: 'auto_upgrade',
      title: 'Auto upgrade',
      desc: `When you hit your usage limit, we move you up to the next plan so your ${unit} keep flowing.`,
      icon: Layers,
    },
    {
      value: 'auto_top_up',
      title: 'Top up as you go',
      desc: `When you are about to hit your usage limit, we add more ${unit} and charge your card. Your plan stays the same.`,
      icon: ArrowUpCircle,
    },
  ];
}

// When a card-less user approves a choice, we stash the exact overflow payload
// here, send them to add a card (new tab), and apply it when the return tab
// lands back on the page. localStorage (not session) so it survives the Stripe
// round-trip in a different tab. Keeps the order explain -> choose -> add card.
const PENDING_OVERFLOW_KEY = 'hmp.overflow.pendingChoice';
// The stash only bridges the add-card redirect. Honor it for a short window so a
// leftover key can't make an unrelated hard refresh show the "setting up"
// placeholder (or re-apply a stale choice). Older keys are dropped on sight.
const PENDING_FRESH_MS = 5 * 60 * 1000;

function readPendingChoice(): { body: unknown; ts: number } | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(PENDING_OVERFLOW_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { body?: unknown; ts?: unknown };
    if (!parsed || typeof parsed.ts !== 'number') return null;
    return { body: parsed.body, ts: parsed.ts };
  } catch {
    return null;
  }
}

// Small "why is there a minimum?" affordance next to the top-up controls.
function InfoHint({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <button
            type="button"
            aria-label={label}
            className="inline-flex items-center justify-center leading-none p-0 text-muted-foreground/60 hover:text-muted-foreground"
          />
        }
      >
        <Info className="size-3.5" />
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{children}</TooltipContent>
    </Tooltip>
  );
}

export function OverflowSwitch({ orgId, sub, onAddCard }: {
  orgId: string | null; sub: BillingSubscription; onAddCard: () => void;
}) {
  // PUT /organizations/:orgId/billing/overflow; on success the hook
  // invalidates the shared subscription key, so the Plans page (and Billing)
  // refetch — this replaces the old onSaved callback.
  const setOverflow = useSetOverflow(orgId);
  const { mutate: putOverflow } = setOverflow;
  const unit: UsageUnit = sub.usageUnit === 'actions' ? 'actions' : 'messages';
  const MODES = modesFor(unit);
  const mode = sub.overflowMode;
  const on = mode !== 'none';

  const [chooseOpen, setChooseOpen] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);
  const [chosenMode, setChosenMode] = useState<ChargingMode>('auto_top_up');
  const [consent, setConsent] = useState(false);
  // Type-to-confirm gate for the destructive disable action.
  const [disableConfirm, setDisableConfirm] = useState('');
  // True from the moment we return with a stashed choice until it is applied, so
  // we show a "setting up" placeholder instead of flashing the off state while
  // the card syncs and the choice is PUT. Seeded from the pending key so it is
  // true on the very first render of the return tab.
  const [applyingPending, setApplyingPending] = useState(() => {
    const p = readPendingChoice();
    return !!p && Date.now() - p.ts < PENDING_FRESH_MS;
  });

  // $/msg. Guard a zero rate so we never divide by zero when computing messages.
  const rate = sub.topUpRateMicros > 0 ? sub.topUpRateMicros / 1_000_000 : 0.001;

  // Upgrade ladder context. `upcomingPlan` is undefined on the top tier (or a
  // custom plan), where auto-upgrade has nothing higher to jump to and falls
  // through to per-message pay-as-you-go, the only case the spend cap applies.
  // AIT-420: a v2 slug is off the legacy ladder, so `nextPlan` returns
  // undefined for BOTH build and scale — read the v2 ladder for those, else a
  // Build org is wrongly treated as topped out and never offered auto-upgrade.
  // AIT-738: a custom plan is its own top tier (the ops-worker never ladders
  // it), so auto-upgrade falls through to top-up exactly like Business.
  const upcomingPlan = sub.isCustomPlan
    ? undefined
    : isV2PlanSlug(sub.plan.slug)
      ? getV2Plan(nextV2PlanSlug(sub.plan.slug) ?? '')
      : nextPlan(sub.plan.slug);
  const atTopPlan = !upcomingPlan;
  const interval = sub.billingInterval ?? 'monthly';
  // The free tier's marketing name ("Launch") reads oddly on a $0 plan; show
  // "Free" there and the marketing name everywhere else.
  const planLabel = (p: { slug: string; name: string }): string =>
    p.slug === 'free' ? 'Free' : p.name;
  const planPriceLabel = (p: { priceInCents: number; annualPriceInCents: number }): string => {
    const cents = interval === 'annual' ? p.annualPriceInCents : p.priceInCents;
    return `$${Math.round(cents / 100)}/${interval === 'annual' ? 'yr' : 'mo'}`;
  };
  // Cap is meaningful only where we charge per message: top-up always, and
  // auto-upgrade once it has laddered to the top plan.
  const showCap = mode === 'auto_top_up' || atTopPlan;
  // At the top plan there's nothing to upgrade to, so auto upgrade collapses into
  // top-up. Offer only the one real choice in the setup modal instead of two
  // cards that do the same thing.
  const availableModes = atTopPlan ? MODES.filter((m) => m.value === 'auto_top_up') : MODES;

  // The "auto upgrade reached the ceiling" notice only makes sense to someone who
  // actually chose auto upgrade and got bumped here — a fresh top-plan top-up user
  // never knew the option existed. Show it only for auto_upgrade, and let them
  // dismiss it for good (mirrors the channel-card-shell dismissible info pattern).
  const TOP_PLAN_NOTICE_KEY = 'hmp:top-plan-autoupgrade-notice-dismissed';
  const [topPlanNoticeDismissed, setTopPlanNoticeDismissed] = useState(() => {
    try { return localStorage.getItem(TOP_PLAN_NOTICE_KEY) === '1'; } catch { return false; }
  });
  function dismissTopPlanNotice() {
    setTopPlanNoticeDismissed(true);
    try { localStorage.setItem(TOP_PLAN_NOTICE_KEY, '1'); } catch { /* private mode — session dismiss still works */ }
  }
  // The smallest top-up is plan-specific: a third of the plan's message quota
  // (backend §3: max($10, quota/3 × rate)). Surfaced as dollars so the field can
  // enforce it instead of a flat $10. ceil so the shown min never rounds below
  // the real floor the backend would bump to. maxDollars keeps the 3-digit cap
  // unless the floor itself is higher (large custom plans).
  const minDollars = Math.max(1, Math.ceil((sub.minRechargeMessages || 0) * rate));
  const maxDollars = Math.max(999, minDollars);
  // Inline config for the active mode. Clamp the initial amount to the plan floor
  // so the field never opens below the minimum.
  const initAmount = Math.max(minDollars, Math.round((sub.rechargeAmountMessages || 0) * rate));
  // Spend cap is expressed to the user as "max top-ups per month" (1-10) and
  // stored as spendCapCents = topUps × per-top-up amount. Derive the initial
  // count back out of the persisted cap; default to 10 (the server's default cap
  // is 10× the recharge amount).
  // What the server ACTUALLY has persisted, before any floor is applied.
  const persistedTopUps = (() => {
    const capD = Math.round((sub.spendCapCents || 0) / 100);
    const n = initAmount > 0 ? Math.round(capD / initAmount) : 0;
    return n < 1 ? 10 : Math.min(10, n); // no cap set yet -> the server default
  })();
  // Show what is actually enforced. The floor governs NEW choices only:
  // silently raising an existing cap would spend more of the customer's money
  // than they authorized, so an existing one-top-up row keeps showing 1 until
  // the customer changes it. AIT-596 owns how we prompt for that.
  const initTopUps = persistedTopUps;
  const initThreshold = sub.thresholdPercent || 25;
  const [amountDollars, setAmountDollars] = useState(initAmount);
  const [maxTopUps, setMaxTopUps] = useState(initTopUps);
  const [threshold, setThreshold] = useState(initThreshold);
  // Baseline of what's persisted. Approval in the choose modal saves these same
  // defaults, so the inline Save button only surfaces once an edit diverges.
  const [saved, setSaved] = useState({ amount: initAmount, topUps: initTopUps, threshold: initThreshold });
  const dirty =
    amountDollars !== saved.amount || maxTopUps !== saved.topUps || threshold !== saved.threshold;

  // Message equivalents shown alongside the dollar/percent inputs so the user
  // sees what they actually get. thresholdMessages mirrors the engine's trigger:
  // floor(threshold% × planQuota) (topup-recharge.service.ts). AIT-544 — it is a
  // share of the PLAN, never of the pack: the pack is floored at $10 on every
  // plan, so on Build it is 50x the quota and a pack-relative sentence quoted a
  // trigger 10x larger than the whole plan, i.e. one the customer never reaches.
  const topUpMessages = Math.max(0, Math.round(amountDollars / rate));
  // v2 orgs carry their quota in `actionsQuota`; legacy orgs in `plan.messages`
  // (which already reflects a custom plan's own quota).
  const planQuota = unit === 'actions' ? (sub.actionsQuota ?? 0) : sub.plan.messages;
  // Capped at what one top-up can strictly clear, mirroring the engine: the pack
  // is floored at a third of quota, so a 40-50% line would need two invoices per
  // trigger. Quote the number the charge actually uses, not the raw percentage.
  const thresholdMessages = Math.min(
    Math.floor((threshold / 100) * planQuota),
    Math.max(0, topUpMessages - 1),
  );

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function buildOverflowBody(targetMode: OverflowMode, withConsent: boolean) {
    // The top-up amount/threshold drive auto_top_up AND auto_upgrade at the top
    // plan (both fall through to the same top_up engine), so send them in both
    // cases — otherwise edits at the top plan silently don't persist.
    const usesTopUpControls = targetMode === 'auto_top_up' || (targetMode === 'auto_upgrade' && atTopPlan);
    return {
      mode: targetMode,
      consent: withConsent,
      rechargeAmountMessages: usesTopUpControls
        ? Math.max(1, Math.round(amountDollars / rate)) : undefined,
      thresholdPercent: usesTopUpControls ? threshold : undefined,
      spendCapCents:
        targetMode !== 'none'
          ? maxTopUps * amountDollars * 100
          : undefined,
    };
  }

  async function persist(targetMode: OverflowMode, withConsent: boolean): Promise<boolean> {
    setError(null);
    setSaving(true);
    try {
      await setOverflow.mutateAsync({ body: buildOverflowBody(targetMode, withConsent) });
      setSaved({ amount: amountDollars, topUps: maxTopUps, threshold });
      return true;
    } catch (err) {
      setError((err as AppError).userMessage ?? 'Could not save');
      return false;
    } finally {
      setSaving(false);
    }
  }

  // On return from adding a card, apply the choice the user already approved.
  useEffect(() => {
    const pending = readPendingChoice();
    // Stale, malformed, or absent: drop any leftover key and clear the
    // placeholder so an old key can't make a plain refresh look like setup.
    if (!pending || Date.now() - pending.ts >= PENDING_FRESH_MS) {
      if (typeof localStorage !== 'undefined') localStorage.removeItem(PENDING_OVERFLOW_KEY);
      setApplyingPending(false);
      return;
    }
    // Fresh, but the card has not synced yet: mid add-card return, keep waiting.
    if (!sub.hasCard) return;
    // Removed immediately so a re-render can't reapply. On success the
    // hook-level invalidation refreshes the subscription, which flips the
    // overflow mode on and clears the placeholder (effect below).
    localStorage.removeItem(PENDING_OVERFLOW_KEY);
    putOverflow(
      { body: pending.body },
      { onError: () => setApplyingPending(false) },
    );
  }, [sub.hasCard, putOverflow]);

  // Clear the placeholder once the applied mode lands (overflow is on), and as a
  // safety net so a failed sync can never leave us stuck on "setting up".
  useEffect(() => {
    if (on) setApplyingPending(false);
  }, [on]);
  useEffect(() => {
    if (!applyingPending) return;
    const t = setTimeout(() => setApplyingPending(false), 8000);
    return () => clearTimeout(t);
  }, [applyingPending]);

  function openSetup() {
    setChosenMode('auto_top_up');
    setConsent(false);
    setError(null);
    setChooseOpen(true);
  }

  function handleToggle(next: boolean) {
    if (next) {
      openSetup();
    } else {
      setDisableConfirm('');
      setError(null);
      setDisableOpen(true);
    }
  }

  function openChange() {
    // Pre-select the other method; switching authorizes a different charge.
    setChosenMode(mode === 'auto_top_up' ? 'auto_upgrade' : 'auto_top_up');
    setConsent(false);
    setError(null);
    setChooseOpen(true);
  }

  async function confirmChoose() {
    // No card yet: remember the approved choice, then send them to add one.
    // It is applied automatically when they come back (see the effect above).
    if (!sub.hasCard) {
      localStorage.setItem(
        PENDING_OVERFLOW_KEY,
        JSON.stringify({ body: buildOverflowBody(chosenMode, true), ts: Date.now() }),
      );
      setChooseOpen(false);
      onAddCard();
      return;
    }
    if (await persist(chosenMode, true)) setChooseOpen(false);
  }

  async function confirmDisable() {
    if (await persist('none', false)) setDisableOpen(false);
  }

  return (
    <section className="flex flex-col gap-3">
      {applyingPending ? (
        // Returning from add-card: applying the approved choice. Hold a calm
        // placeholder instead of flashing the off state before it lands.
        <div className="flex items-center gap-2 rounded-xl bg-muted px-4 py-3 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          Setting up automated billing...
        </div>
      ) : !sub.hasCard ? (
        // No card yet: explain what automated billing does and let them choose
        // first. Approving in the modal is what sends them to add a card, so the
        // card request lands AFTER they understand why they need it.
        <div className="flex w-full items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-50 px-4 py-3 text-amber-900 sm:items-center dark:bg-amber-950/30 dark:text-amber-200">
          <TriangleAlert className="size-4 shrink-0 translate-y-0.5 text-amber-600 sm:translate-y-0 dark:text-amber-400" />
          <div className="flex flex-1 flex-wrap items-center justify-between gap-x-4 gap-y-2">
            <p className="min-w-[min(28ch,100%)] flex-1 text-sm">
              Automated billing is off. All channels will be disabled when you reach your usage limit.
            </p>
            <Button
              size="sm"
              variant="outline"
              onClick={openSetup}
              className="shrink-0 border-amber-500/40 bg-transparent hover:bg-amber-100 dark:hover:bg-amber-900/40"
            >
              Set Up
            </Button>
          </div>
        </div>
      ) : (
        <>
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <h3 className="text-sm font-semibold">Automated billing</h3>
          {!on && (
            <p className="text-xs text-muted-foreground">Off. Channels pause at your limit.</p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {/* No mode choice at the top plan: auto upgrade and top-up collapse to
              the same behavior there, so "Change" has nothing to switch between. */}
          {on && !atTopPlan && (
            <Button variant="outline" size="sm" onClick={openChange} disabled={saving}>
              Change
            </Button>
          )}
          <Switch checked={on} onCheckedChange={handleToggle} aria-label="Automated billing" />
        </div>
      </div>

      {!on && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
          <TriangleAlert className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-400" />
          <p className="leading-relaxed">
            All channels will be disabled when you reach your usage limit. Sending and
            receiving stop.
          </p>
        </div>
      )}

      {on && (
        <div className="flex flex-col gap-3 rounded-xl bg-muted p-4">
          <h4 className="text-sm font-semibold">
            {atTopPlan ? 'Top up as you go' : MODES.find((m) => m.value === mode)?.title}
          </h4>

          {/* Only relevant to someone who chose auto upgrade and laddered up to the
              top plan: explain why it's now top-up. Dismissible; hidden for users
              who picked top-up directly (they never saw the auto upgrade option). */}
          {atTopPlan && mode === 'auto_upgrade' && !topPlanNoticeDismissed && (
            <Alert className="relative border-blue-500/30 bg-blue-500/5 pr-10">
              <Info className="size-4 text-blue-500" />
              <AlertDescription>
                You reached the highest plan, so there is nothing higher for auto upgrade to move
                you to. We keep your channels running with automated top-ups instead. Contact us
                for a custom offer.
              </AlertDescription>
              <button
                type="button"
                onClick={dismissTopPlanNotice}
                aria-label="Dismiss"
                className="absolute right-2 top-2 inline-flex size-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-blue-500/10 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50"
              >
                <X className="size-4" />
              </button>
            </Alert>
          )}

          {/* Upgrade ladder — only when there is a higher plan to move to. */}
          {mode === 'auto_upgrade' && upcomingPlan && (
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <span className="rounded-lg border border-border bg-background px-2.5 py-1 text-sm font-medium">
                  {planLabel(sub.plan)}
                </span>
                <ArrowRight className="size-4 shrink-0 text-muted-foreground" />
                <span className="rounded-lg border border-primary/40 bg-accent/40 px-2.5 py-1 text-sm font-medium text-foreground">
                  {planLabel(upcomingPlan)}
                  <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                    {planPriceLabel(upcomingPlan)}
                  </span>
                </span>
              </div>
              <p className="text-sm leading-relaxed text-muted-foreground">
                When you hit your usage limit, we move you up to the next plan so your channels keep
                running.
              </p>
            </div>
          )}

          {/* Top-up controls — auto_top_up, or auto_upgrade at the top plan (both
              buy bundles via the same engine, using these same settings). */}
          {(mode === 'auto_top_up' || atTopPlan) && (
            <div className="flex flex-col gap-2">
              <div className="flex flex-wrap items-center gap-x-2.5 gap-y-2 text-sm">
                <span className="flex items-center gap-1">
                  <Label className="text-sm">Top up by</Label>
                  <InfoHint label="Why is there a minimum top-up amount?">
                    Each top-up is at least about a third of your plan&apos;s monthly {unit}, so one
                    recharge can absorb a sudden burst of traffic instead of being used up at once and
                    charging you again moments later.
                  </InfoHint>
                </span>
                <div className="relative w-20">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
                  <Input
                    id="topup-amount"
                    aria-label="Top up amount in dollars"
                    type="number"
                    min={minDollars}
                    max={maxDollars}
                    value={amountDollars}
                    onChange={(e) => {
                      // Cap on every keystroke so the live message count can't
                      // balloon; the plan-floor min is only enforced on blur so the
                      // user can still type intermediate values.
                      const n = Number(e.target.value);
                      setAmountDollars(Number.isFinite(n) ? Math.min(maxDollars, Math.max(0, Math.round(n))) : 0);
                    }}
                    onBlur={(e) => {
                      const n = Number(e.target.value);
                      setAmountDollars(Number.isFinite(n) ? Math.min(maxDollars, Math.max(minDollars, Math.round(n))) : minDollars);
                    }}
                    className="pl-6 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                  />
                </div>
                <span className="flex items-center gap-1">
                  <Label className="text-sm">when my usage reaches</Label>
                  <InfoHint label="Why can't I trigger top-ups earlier?">
                    You can&apos;t trigger earlier than 50% used. Any sooner and a fresh top-up could
                    immediately qualify to trigger the next one, so we cap it here to keep recharges
                    from looping.
                  </InfoHint>
                </span>
                {/* Stored value is "% left"; we show its inverse ("% reached") so the
                    sentence reads the way users think about it. */}
                <Select
                  value={String(100 - threshold)}
                  onValueChange={(v) => v && setThreshold(100 - Number(v))}
                >
                  <SelectTrigger aria-label="Top up when my usage reaches this percent" className="w-20">
                    <SelectValue>{(v) => (v ? `${v}%` : '')}</SelectValue>
                  </SelectTrigger>
                  <SelectContent className="min-w-20">
                    {[50, 60, 70, 75, 80].map((reached) => (
                      <SelectItem key={reached} value={String(reached)}>{reached}%</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-muted-foreground">
                ≈ {topUpMessages.toLocaleString()} {unit} each time, topped up when about{' '}
                {thresholdMessages.toLocaleString()} are left.
              </p>
            </div>
          )}

          {showCap && (
            <div className="flex flex-col gap-1.5 border-t border-border pt-3">
              <div className="flex flex-wrap items-center gap-x-2.5 gap-y-2 text-sm">
                <Label className="text-sm">Maximum top-ups per month</Label>
                <Select value={String(maxTopUps)} onValueChange={(v) => v && setMaxTopUps(Number(v))}>
                  <SelectTrigger aria-label="Maximum top-ups per month" className="w-16">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="min-w-16">
                    {/* AIT-594: starts at 2. One top-up is spent in full by the
                        first purchase, which pauses the channels the customer
                        just paid to keep running. */}
                    {/* Offers the floor upward, but never hides a customer's
                        existing lower choice: a persisted 1 stays visible and
                        selected until they raise it themselves. */}
                    {Array.from(
                      { length: 10 - Math.min(MIN_TOP_UPS, initTopUps) + 1 },
                      (_, i) => i + Math.min(MIN_TOP_UPS, initTopUps),
                    ).map((n) => (
                      <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-muted-foreground">
                Once you reach this, we pause your channels so you are not charged more.
              </p>
            </div>
          )}

          {error && !chooseOpen && !disableOpen && (
            <p role="alert" className="text-sm text-destructive">{error}</p>
          )}

          {dirty && (
            <Button onClick={() => persist(mode, true)} disabled={saving} className="self-start">
              {saving && <Loader2 className="size-3.5 animate-spin" />}
              Save changes
            </Button>
          )}
        </div>
      )}
        </>
      )}

      {/* Choose / change method */}
      <Dialog open={chooseOpen} onOpenChange={setChooseOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{on ? 'Change billing method' : 'Turn on automated billing'}</DialogTitle>
            <DialogDescription>
              {atTopPlan
                ? `When you reach your usage limit, we add more ${unit} and charge your card.`
                : 'Pick what happens when you reach your usage limit.'}
            </DialogDescription>
          </DialogHeader>

          <div className={cn('grid gap-3', availableModes.length > 1 && 'sm:grid-cols-2')}>
            {availableModes.map((m) => {
              const selected = chosenMode === m.value;
              const Icon = m.icon;
              return (
                <button
                  key={m.value}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => setChosenMode(m.value)}
                  className={cn(
                    'flex flex-col gap-2 rounded-xl border p-4 text-left transition-colors',
                    selected
                      ? 'border-primary bg-accent/40 ring-1 ring-primary'
                      : 'border-border hover:bg-accent/30',
                  )}
                >
                  <Icon className={cn('size-5', selected ? 'text-primary' : 'text-muted-foreground')} />
                  <span className="text-base font-medium text-foreground">{m.title}</span>
                  <span className="text-sm leading-relaxed text-muted-foreground">{m.desc}</span>
                </button>
              );
            })}
          </div>

          <Label
            htmlFor="choose-consent"
            className="flex items-start gap-2.5 text-sm font-normal leading-relaxed text-muted-foreground"
          >
            <Checkbox id="choose-consent" checked={consent} onCheckedChange={setConsent} className="mt-0.5" />
            <span>
              I authorize HookMyApp to charge my card for {unit === 'actions' ? 'action top-ups' : 'message top-ups'}{' '}
              when this takes effect, up to my spend cap.{' '}
              {chosenMode === 'auto_upgrade' && (
                <>
                  Once I reach the top plan there is nothing higher to move to, so HookMyApp
                  keeps my channels running by adding more {unit} (priced at ${rate.toFixed(3)} each)
                  and charging my card, up to my spend cap.{' '}
                </>
              )}
              I can change or cancel this anytime.
            </span>
          </Label>

          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setChooseOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={confirmChoose} disabled={saving || !consent}>
              {saving && <Loader2 className="size-3.5 animate-spin" />}
              {!sub.hasCard ? 'Approve & Add Card' : on ? 'Approve & Switch' : 'Approve & Turn On'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm disable */}
      <AlertDialog
        open={disableOpen}
        onOpenChange={(nextOpen) => {
          if (saving) return;
          setDisableOpen(nextOpen);
          if (!nextOpen) setDisableConfirm('');
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Turn off automated billing?</AlertDialogTitle>
            <AlertDialogDescription>
              All channels will be disabled when you reach your usage limit. Sending and
              receiving stop.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="grid gap-2 px-0">
            <label className="text-sm font-medium">
              Type &ldquo;disable&rdquo; to confirm:
            </label>
            <Input
              type="text"
              value={disableConfirm}
              onChange={(e) => setDisableConfirm(e.target.value)}
              aria-label="Type disable to confirm turning off automated billing"
              autoComplete="off"
            />
          </div>

          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>Keep It On</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={saving || disableConfirm !== 'disable'}
              onClick={confirmDisable}
            >
              {saving && <Loader2 className="size-3.5 animate-spin" />}
              Turn Off
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
